// Função utilitária para remover acentos e normalizar string
function normalizeString(str: string): string {
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove acentos
    .replace(/[^\w\s]/g, '') // Remove caracteres especiais, exceto letras/números/espacos
    .toLowerCase();
}

// Função para filtrar revistas pelo nome (article_title), ignorando acentos/case
export function filterMagazinesByName(magazines: any[], searchTerm: string, field: string = 'article_title') {
  const normalizedTerm = normalizeString(searchTerm);
  return magazines.filter((mag) => {
    const value = mag[field] || '';
    return normalizeString(value).includes(normalizedTerm);
  });
}
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation, UseMutationOptions, useQuery } from 'react-query';
import { useAPICallAsMutation } from '../hooks';
import { IBook, PaginatedResponse, Params } from '../types';
import {
  getBookActivities,
  getBookCategories,
  getBooks,
  getFavoritedBooks,
  getMyBooks,
  getExchangeableBooks,
  getReceivedBooks,
  getMyBooksDetail,
  getReadingBooks,
  getReceivedBooksMonth,
  getBooksMonth,
  favoriteBook,
  unFavoriteBook,
  setReadingBook,
  getReceivedBooksMonthBestSellers,
  getBooksDetail,
  getMonthBestSellers,
  getReadingBooksDetail,
  getDownloadURL,
  getDigitalMagazines,
} from './api';

export enum BookstoreHook {
  Activities = 'activities',
  Books = 'books',
  CombinedBooks = 'combined-books',
  FeaturedBooks = 'featured-books',
  CombinedFeaturedBooks = 'combined-featured-books',
  Categories = 'categories',
  MyBooks = 'my-books',
  GetBook = 'get-book',
  GetExchangeableBooks = 'get-exchangeable-books',
  GetMyBooksDetail = 'get-my-books-detail',
  GetReadingBooksDetail = 'get-reading-books-detail',
  Received = 'received',
  ReceivedMothly = 'received-monthly',
  BestSellersMothlyReceived = 'received-monthly-best-sellers',
  BestSellersMothly = 'monthly-best-sellers',
}

export function useBookCategories() {
  return useQuery([BookstoreHook.Categories], () => getBookCategories());
}

export function useBookActivities() {
  return useQuery([BookstoreHook.Activities], () => getBookActivities());
}

export function useBooks(params?: Record<string, string>) {
  return useQuery([BookstoreHook.Books, params], () => getBooks(params));
}

function getTypedParams(params: Params = {}, type: 'book' | 'audiobook'): Record<string, string> {
  const nextParams = { ...params } as Record<string, string>;

  delete nextParams.type;
  delete nextParams.audiobook;

  return { ...nextParams, type };
}

function mergePaginatedBooksResponses(
  responses: PaginatedResponse<IBook>[]
): PaginatedResponse<IBook> {
  const [firstResponse] = responses;

  return {
    ...firstResponse,
    count: responses.reduce((total, response) => total + (response.count || 0), 0),
    next: responses.find((response) => response.next)?.next || null,
    previous: responses.find((response) => response.previous)?.previous || null,
    results: responses.flatMap((response) => response.results || []),
  };
}

async function getCombinedBooks(params: Params = {}): Promise<PaginatedResponse<IBook>> {
  const [booksResponse, audiobooksResponse] = await Promise.all([
    getBooks(getTypedParams(params, 'book')),
    getBooks(getTypedParams(params, 'audiobook')),
  ]);

  return mergePaginatedBooksResponses([booksResponse, audiobooksResponse]);
}

async function getCombinedFeaturedBooks(params: Params = {}): Promise<PaginatedResponse<IBook>> {
  const [booksResponse, audiobooksResponse] = await Promise.all([
    getBooksMonth(getTypedParams(params, 'book')),
    getBooksMonth(getTypedParams(params, 'audiobook')),
  ]);

  return mergePaginatedBooksResponses([booksResponse, audiobooksResponse]);
}

const MAGAZINE_MONTH_NAMES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

async function getCombinedReceivedMagazines(
  params: Record<string, string> = {}
): Promise<{ label: string; books: any[] }[]> {
  const { agreement, ...receivedParams } = params;

  const [receivedResponse, digitalResponse] = await Promise.all([
    getReceivedBooksMonth({ ...receivedParams, book__type: 'magazine' }),
    getDigitalMagazines({ agreement, limit: '100' }).catch(() => ({ results: [] as any[] }))
  ]);

  const digitalMagazines = (digitalResponse.results || []).filter(
    (m: any) => m.cover && (m.fileUrl || m.pdf || m.file || m.link)
  );

  const digitalGrouped = new Map<string, any[]>();
  for (const magazine of digitalMagazines) {
    // Extrai mês/ano do article_title se necessário
    let monthRef: number | undefined = magazine.referenceMonth ?? magazine.reference_month;
    let yearRef: number | undefined = magazine.referenceYear ?? magazine.reference_year;
    const title = magazine.article_title || magazine.articleTitle || magazine.title || '';

    // Se não veio o mês/ano, tenta extrair do título
    if ((monthRef === undefined || yearRef === undefined) && title) {
      // Exemplo: "Revista Janeiro 2026"
      const regex = /(janeiro|fevereiro|março|abril|maio|junho|julho|agosto|setembro|outubro|novembro|dezembro)\s+(\d{4})/i;
      const match = title.match(regex);
      if (match) {
        const meses = [
          'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
          'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
        ];
        monthRef = meses.findIndex(m => m === match[1].toLowerCase());
        yearRef = Number(match[2]);
      }
    }

    // Se não conseguiu identificar mês/ano, vai para grupo "sem-ref"
    const key = monthRef !== undefined && yearRef !== undefined ? `${yearRef}-${String(monthRef).padStart(2, '0')}` : 'sem-ref';

    const wrapped = {
      id: magazine.id,
      book: {
        ...magazine,
        type: 'magazine',
        levels: magazine.levels || [],
        title: magazine.title || magazine.articleTitle || magazine.name || '',
        author: magazine.author || magazine.agreement || '',
        isbn: magazine.isbn || magazine.issn || '',
      },
      createdAt: magazine.createdAt || '',
      description: magazine.description || '',
      document: '',
      exchanged: false,
      exchangedBook: false,
      exchangedDate: false,
      hashCode: magazine.hashCode || '',
      monthReference: monthRef,
      yearReference: yearRef,
    };

    if (!digitalGrouped.has(key)) digitalGrouped.set(key, []);
    digitalGrouped.get(key)!.push(wrapped);
  }

  const merged: { label: string; books: any[] }[] = (receivedResponse || []).map((group) => ({
    label: group.label,
    books: [...group.books],
  }));

  for (const [key, books] of digitalGrouped.entries()) {
    if (key === 'sem-ref') {
      merged.push({ label: 'Outras Revistas Personalizadas', books });
      continue;
    }

    const [yearStr, monthStr] = key.split('-');
    const year = Number(yearStr);
    const month = Number(monthStr);

    // Procura grupo existente, se não existir cria
    let existing = merged.find((g) => {
      // Se não tem books, não bate
      if (!g.books.length) return false;
      let bMonth = g.books[0].monthReference ?? g.books[0].referenceMonth ?? g.books[0].reference_month;
      let bYear = g.books[0].yearReference ?? g.books[0].referenceYear ?? g.books[0].reference_year;
      return bMonth === month && bYear === year;
    });

    if (existing) {
      existing.books = [...existing.books, ...books];
    } else {
      const label = `${MAGAZINE_MONTH_NAMES[Math.max(0, Math.min(11, month))]} ${year}`;
      merged.push({ label, books });
    }
  }

  merged.sort((a, b) => {
    const ra: any = a.books[0];
    const rb: any = b.books[0];
    const ay = ra?.yearReference || 0;
    const by = rb?.yearReference || 0;
    if (ay !== by) return by - ay;
    const am = ra?.monthReference || 0;
    const bm = rb?.monthReference || 0;
    return bm - am;
  });

  return merged;
}

export function useCombinedBooks(params?: Record<string, string>) {
  return useQuery([BookstoreHook.CombinedBooks, params], () => getCombinedBooks(params));
}

export function useGetBooksMutation(options: UseMutationOptions<PaginatedResponse<IBook>, unknown, Params>) {

  return useMutation((params: Params) => getBooks({ ...params } as Record<string, string>), options);
}

export function useGetCombinedBooksMutation(
  options: UseMutationOptions<PaginatedResponse<IBook>, unknown, Params>
) {
  return useMutation((params: Params) => getCombinedBooks(params), options);
}

export function useMyBooks(params?: Record<string, string>) {
  return useQuery([BookstoreHook.MyBooks, params], () => getMyBooks(params));
}

export function useExchangeableBooks(options: UseMutationOptions<PaginatedResponse<IBook>, unknown, Params>) {
  return useMutation((params: Params) => getExchangeableBooks(params), options);
}

export function useGetMyBooksMutation(options: UseMutationOptions<PaginatedResponse<IBook>, unknown, Params>) {
  return useMutation((params: Params) => getMyBooks(params as Record<string, string>), options);
}

export function useMyReadingBooks(options: UseMutationOptions<PaginatedResponse<IBook>, unknown, Params>) {
  return useAPICallAsMutation(getReadingBooks, options);
}

export function useGetAudioBooks(options: UseMutationOptions<PaginatedResponse<IBook>, unknown, Params>) {

  return useMutation((params: Params) => getBooks({ ...params, audiobook: 'true' } as Record<string, string>), options);
}

export function useGetFavoritedBooksMutation(options: UseMutationOptions<PaginatedResponse<IBook>, unknown, Params>) {
  return useAPICallAsMutation(getFavoritedBooks, options);
}

export function useReceivedBooks(params?: Record<string, string>) {
  return useQuery([BookstoreHook.Received, params], () => getReceivedBooks(params));
}

export function useReceivedMothly(params?: Record<string, string>) {
  return useQuery([BookstoreHook.ReceivedMothly, params], () => getReceivedBooksMonth(params));
}

export function useCombinedReceivedMagazines(params?: Record<string, string>) {
  return useQuery(['combined-received-magazines', params], () => getCombinedReceivedMagazines(params));
}

export function useMothlyBestSellers(params?: Record<string, string>) {
  return useQuery([BookstoreHook.BestSellersMothly, params], () => getMonthBestSellers(params));
}

export function useReceivedMothlyBestSellers(params?: Record<string, string | boolean>) {
  const stringParams = params
    ? Object.fromEntries(Object.entries(params).map(([key, value]) => [key, String(value)]))
    : undefined;
  return useQuery([BookstoreHook.BestSellersMothlyReceived, stringParams], () => getReceivedBooksMonthBestSellers(stringParams));
}

export function useGetBookDetail(bookId: string, options?: any) {
  return useQuery([BookstoreHook.GetMyBooksDetail, bookId], () => getBooksDetail(bookId), options);
}

export function useGetReadingBooksDetail(bookId: string, options?: any) {
  return useQuery([BookstoreHook.GetReadingBooksDetail, bookId], () => getReadingBooksDetail(bookId), options);
}


export function useGetMyBooksDetail(bookId: string, options?: any) {
  return useQuery([BookstoreHook.GetMyBooksDetail, bookId], () => getMyBooksDetail(bookId), options);
}

export function useFeaturedBooks(params?: Record<string, string>) {

  return useQuery([BookstoreHook.FeaturedBooks, params], () => getBooksMonth(params));
}

export function useCombinedFeaturedBooks(params?: Record<string, string>) {
  return useQuery([BookstoreHook.CombinedFeaturedBooks, params], () => getCombinedFeaturedBooks(params));
}

export function useDigitalMagazines(params?: Record<string, string>) {
  return useQuery(['digital-magazines', params], () => getDigitalMagazines(params), {
    enabled: !!params?.agreement,
  });
}

export function useUnFavoriteBookMutation(options: UseMutationOptions<IBook, unknown, number>) {
  return useMutation((bookId: number) => unFavoriteBook(bookId).then((response) => response.data as IBook), options);
}

export function useFavoriteBookMutation(options: UseMutationOptions<IBook, unknown, number>) {
  return useMutation((bookId: number) => favoriteBook(bookId).then((response) => response.data as IBook), options);
}

export function useDownloadMutation(
  options: UseMutationOptions<string, unknown, { bookId: number; productId: number }>
) {
  return useMutation(({ bookId, productId }: { bookId: number; productId: number }) => getDownloadURL(bookId, productId), options);
}

export function useGetBookReadingMutation(options: UseMutationOptions<IBook, unknown, number>) {
  return useMutation((bookId: number) => setReadingBook(bookId), options);
}
